import { Typography } from 'antd';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import AlumlyDataGrid from '../../../components/AlumlyDataGrid';
import { PermissionEnum } from '../../../enums/permission';
import { RouteNameEnum } from '../../../enums/routeName';
import { ServiceNameEnum } from '../../../enums/serviceName';
import usePermission from '../../../hooks/usePermission';
import useServices from '../../../hooks/useServices';
import { AxiosService } from '../../../utils/axiosService';
import { PathBuilder } from '../../../utils/pathBuilder';
import FormFilterNotificationList, { FilterNotificationT } from './FormFilterNotificationList';

const USER_TYPE_LABELS: Record<string, string> = {
  alumni: 'ศิษย์เก่า',
  bba_student: 'นักศึกษา',
  faculty: 'อาจารย์',
  employer: 'Employer',
};

export interface NotificationListT {
  id: number;
  title: string;
  message: string;
  createdAt: string;
  createdBy: string;
  targetUserTypes: string[] | null;
}

const NotificationList: React.FC = () => {
  const navigate = useNavigate();
  const { checkPermission } = usePermission();
  const [isLoading, setIsLoading] = useState(false);

  const [notifications, setNotifications] = useState<NotificationListT[]>([]);
  const [selectedFilter, setSelectedFilter] = useState<FilterNotificationT>();
  const [pagination, setPagination] = useState({
    page: 0,
    pageSize: 10,
    rowCount: 0,
    loading: false,
  });

  const { service: getNotificationService } = useServices(ServiceNameEnum.notification.index);

  const getNotifications = async (): Promise<void> => {
    const pageOptions = { page: pagination.page + 1 };
    const filterOptions = selectedFilter;
    const queryString = { ...pageOptions, ...filterOptions };
    const { data, error } = await getNotificationService.get({ queryString });
    if (data) {
      setNotifications(data.items);
      setPagination({
        page: data.pagination.currentPage - 1,
        pageSize: data.pagination.perPage,
        rowCount: data.pagination.total,
        loading: false,
      });
    } else if (error) {
      const errorResponse = AxiosService.getErrorResponse(error);
      toast.error(errorResponse?.message, { toastId: 'allToast' });
    }
  };

  const columns: any[] = [
    {
      field: 'header',
      headerName: 'Title',
      width: 200,
      cellClassName: 'text-wrap',
      valueGetter: (params: any) => params.row.header,
    },
    {
      field: 'body',
      headerName: 'Message',
      width: 450,
      flex: 1,
      cellClassName: 'text-wrap',
      valueGetter: (params: any) => params.row.body,
    },
    {
      field: 'createdBy',
      headerName: 'Created by',
      width: 200,
      cellClassName: 'text-wrap',
      valueGetter: (params: any) => params.row.createdBy,
    },
    {
      field: 'targetUserTypes',
      headerName: 'ส่งถึง',
      width: 220,
      cellClassName: 'text-wrap',
      valueGetter: (params: any) => {
        const types: string[] | null = params.row.targetUserTypes;
        if (!types || types.length === 0) return 'ทุกคน';
        return types.map((t: string) => USER_TYPE_LABELS[t] ?? t).join(', ');
      },
    },
    {
      field: 'createdAt',
      headerName: 'Date created',
      width: 180,
      valueGetter: (params: any) =>
        `${params.row.createdAt}`,
    },
  ];

  const onRowClick = (rowData: any): void => {
    const params = { id: rowData.id };
    const detailPath = PathBuilder.buildPath(RouteNameEnum.notification.detail, { params });
    navigate(detailPath);
  };

  const resetPage = (): void => {
    setPagination({
      ...pagination,
      page: 0,
    });
  };

  useEffect(() => {
    setPagination((prev) => ({ ...prev, loading: true }));
    getNotifications();
  }, [pagination.page, selectedFilter]);

  return (
    <div style={{ marginTop: 32 }}>
      <FormFilterNotificationList
        resetPage={resetPage}
        selectedFilter={selectedFilter}
        setSelectedFilter={setSelectedFilter}
      />
      <div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: 32,
            marginBottom: 24,
          }}
        >
          <div style={{ display: 'flex' }}>
            <Typography.Text style={{ marginRight: 8 }}>Total</Typography.Text>
            <Typography.Text strong>{`${pagination.rowCount} Items`}</Typography.Text>
          </div>
        </div>
      </div>
      <AlumlyDataGrid
        rowHeight="auto"
        rows={notifications}
        columns={columns}
        height={500}
        onRowClick={
              checkPermission([PermissionEnum.code.notificationViewDetail])
                ? onRowClick
                : undefined
            }
        {...pagination}
        paginationMode="server"
        onPageChange={(page: number) => setPagination((prev) => ({ ...prev, page }))}
      />
    </div>
  );
};

export default NotificationList;
